// WP-3.7 (M19 TL-1/2, S-29 `/timeline`): the vertical, day-grouped feed — icon + sentence +
// entity link per row, type filter chips. Shared by S-29 itself and the party/project-scoped
// "Timeline" tabs TL-1's own "three scopes" language calls for (S-20 Overview, S-24 Timeline).
import { TIMELINE_ENTITY_TYPES, type TimelineEntityType } from '@pfos/engine';
import type { TimelineEntryResponse } from '@pfos/shared';
import {
  ArrowRightLeft,
  Briefcase,
  CalendarClock,
  FileText,
  HandCoins,
  History,
  Receipt,
  Shield,
  Bell,
  Coins,
  Repeat,
  Tag,
  Target,
  Upload,
  User,
  Wallet,
  Lightbulb,
  Folder,
  type LucideIcon,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTimeline, type TimelineFilters } from '../lib/timeline';
import { Amount } from './Amount';
import { EmptyState } from './EmptyState';

const ICONS: Record<TimelineEntityType, LucideIcon> = {
  transaction: Receipt,
  transfer: ArrowRightLeft,
  rp: HandCoins,
  tpfCase: Shield,
  quotation: FileText,
  project: Briefcase,
  scheduleItem: CalendarClock,
  // WP-H2: the eleven types the feed previously could not show at all.
  party: User,
  goal: Target,
  reserve: Shield,
  suggestion: Lightbulb,
  account: Wallet,
  category: Folder,
  tag: Tag,
  fxRate: Coins,
  importSource: Upload,
  recurringTemplate: Repeat,
  reminder: Bell,
};

// No dedicated detail screen exists for a transfer, a receivable/payable, or a schedule item
// (S-15 Owed and S-14 Expected are LISTS only) — link to the closest real screen rather than
// inventing a route.
const LINK_PATHS: Record<TimelineEntityType, (id: string) => string> = {
  transaction: (id) => `/tx/${id}`,
  transfer: () => '/tx',
  rp: () => '/owed',
  tpfCase: (id) => `/tpf/${id}`,
  quotation: (id) => `/quotes/${id}`,
  project: (id) => `/projects/${id}`,
  scheduleItem: () => '/expected',
  // WP-H2: same discipline as the originals — link to the closest REAL screen (verified against
  // routes.ts), never invent a route that would 404.
  party: (id) => `/parties/${id}`,
  goal: () => '/goals',
  reserve: () => '/goals',
  suggestion: () => '/advisor',
  account: (id) => `/accounts/${id}`,
  category: () => '/settings',
  tag: () => '/settings',
  fxRate: () => '/settings',
  importSource: () => '/import',
  recurringTemplate: () => '/recurring',
  reminder: () => '/reminders',
};

const TYPE_LABELS: Record<TimelineEntityType, string> = {
  transaction: 'Transactions',
  transfer: 'Transfers',
  rp: 'Owed',
  tpfCase: 'Third-party',
  quotation: 'Quotations',
  project: 'Projects',
  scheduleItem: 'Schedule',
  party: 'Contacts',
  goal: 'Goals',
  reserve: 'Reserves',
  suggestion: 'Advice',
  account: 'Accounts',
  category: 'Categories',
  tag: 'Tags',
  fxRate: 'FX rates',
  importSource: 'Import',
  recurringTemplate: 'Recurring',
  reminder: 'Reminders',
};

function groupByDay(
  entries: TimelineEntryResponse[],
): { day: string; entries: TimelineEntryResponse[] }[] {
  const groups: { day: string; entries: TimelineEntryResponse[] }[] = [];
  for (const entry of entries) {
    const day = new Date(entry.at).toLocaleDateString(undefined, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    const last = groups[groups.length - 1];
    if (last?.day === day) last.entries.push(entry);
    else groups.push({ day, entries: [entry] });
  }
  return groups;
}

export function TimelineFeed({
  filters = {},
  showTypeFilter = true,
}: {
  filters?: TimelineFilters;
  showTypeFilter?: boolean;
}): React.JSX.Element {
  const [activeTypes, setActiveTypes] = useState<TimelineEntityType[]>([]);
  const { data: entries, isLoading } = useTimeline({
    ...filters,
    ...(activeTypes.length > 0 ? { entityTypes: activeTypes } : {}),
  });

  return (
    <div>
      {showTypeFilter && (
        <div className="mb-3 flex flex-wrap gap-2">
          {TIMELINE_ENTITY_TYPES.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => {
                setActiveTypes((prev) =>
                  prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t],
                );
              }}
              className={`rounded-full border px-2 py-1 text-xs ${
                activeTypes.includes(t)
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border text-muted hover:text-ink'
              }`}
            >
              {TYPE_LABELS[t]}
            </button>
          ))}
        </div>
      )}

      {isLoading && <p className="text-sm text-muted">Loading…</p>}

      {!isLoading && (entries === undefined || entries.length === 0) && (
        <EmptyState icon={History} message="Nothing here yet." />
      )}

      {entries !== undefined && entries.length > 0 && (
        <div className="space-y-4">
          {groupByDay(entries).map((group) => (
            <div key={group.day}>
              <p className="mb-2 text-xs font-semibold uppercase text-muted">{group.day}</p>
              <ul className="space-y-2">
                {group.entries.map((entry) => {
                  const Icon = ICONS[entry.entityType];
                  return (
                    <li
                      key={entry.id}
                      className="flex items-center gap-3 rounded-[var(--radius-card)] border border-border bg-surface p-3"
                    >
                      <Icon size={16} className="shrink-0 text-muted" />
                      <Link
                        to={LINK_PATHS[entry.entityType](entry.entityId)}
                        className="flex-1 text-sm text-ink hover:underline"
                      >
                        {entry.sentence}
                      </Link>
                      {entry.amountMinor !== undefined && entry.currency !== undefined && (
                        <Amount
                          minorUnits={entry.amountMinor}
                          currency={entry.currency}
                          className="text-sm font-medium"
                        />
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
